/**
 * This code was AUTOGENERATED using the codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type IAccountMeta,
  type IInstruction,
  type IInstructionWithAccounts,
  type IInstructionWithData,
  type ReadonlyAccount,
} from '@solana/kit';
import { LIGHTHOUSE_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  LogLevel,
  getBubblegumTreeConfigAssertionDecoder,
  getBubblegumTreeConfigAssertionEncoder,
  getLogLevelDecoder,
  getLogLevelEncoder,
  type BubblegumTreeConfigAssertion,
  type BubblegumTreeConfigAssertionArgs,
  type LogLevelArgs,
} from '../types';

export const ASSERT_BUBBLEGUM_TREE_CONFIG_ACCOUNT_DISCRIMINATOR = 17;

export function getAssertBubblegumTreeConfigAccountDiscriminatorBytes() {
  return getU8Encoder().encode(
    ASSERT_BUBBLEGUM_TREE_CONFIG_ACCOUNT_DISCRIMINATOR
  );
}

export type AssertBubblegumTreeConfigAccountInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountTargetAccount extends string | IAccountMeta<string> = string,
  TRemainingAccounts extends readonly IAccountMeta<string>[] = [],
> = IInstruction<TProgram> &
  IInstructionWithData<Uint8Array> &
  IInstructionWithAccounts<
    [
      TAccountTargetAccount extends string
        ? ReadonlyAccount<TAccountTargetAccount>
        : TAccountTargetAccount,
      ...TRemainingAccounts,
    ]
  >;

export type AssertBubblegumTreeConfigAccountInstructionData = {
  discriminator: number;
  logLevel: LogLevel;
  assertion: BubblegumTreeConfigAssertion;
};

export type AssertBubblegumTreeConfigAccountInstructionDataArgs = {
  logLevel?: LogLevelArgs;
  assertion: BubblegumTreeConfigAssertionArgs;
};

export function getAssertBubblegumTreeConfigAccountInstructionDataEncoder(): Encoder<AssertBubblegumTreeConfigAccountInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['logLevel', getLogLevelEncoder()],
      ['assertion', getBubblegumTreeConfigAssertionEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: ASSERT_BUBBLEGUM_TREE_CONFIG_ACCOUNT_DISCRIMINATOR,
      logLevel: value.logLevel ?? LogLevel.Silent,
    })
  );
}

export function getAssertBubblegumTreeConfigAccountInstructionDataDecoder(): Decoder<AssertBubblegumTreeConfigAccountInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['logLevel', getLogLevelDecoder()],
    ['assertion', getBubblegumTreeConfigAssertionDecoder()],
  ]);
}

export function getAssertBubblegumTreeConfigAccountInstructionDataCodec(): Codec<
  AssertBubblegumTreeConfigAccountInstructionDataArgs,
  AssertBubblegumTreeConfigAccountInstructionData
> {
  return combineCodec(
    getAssertBubblegumTreeConfigAccountInstructionDataEncoder(),
    getAssertBubblegumTreeConfigAccountInstructionDataDecoder()
  );
}

export type AssertBubblegumTreeConfigAccountInput<
  TAccountTargetAccount extends string = string,
> = {
  /** Target mpl-bubblegum tree config account to be asserted */
  targetAccount: Address<TAccountTargetAccount>;
  logLevel?: AssertBubblegumTreeConfigAccountInstructionDataArgs['logLevel'];
  assertion: AssertBubblegumTreeConfigAccountInstructionDataArgs['assertion'];
};

export function getAssertBubblegumTreeConfigAccountInstruction<
  TAccountTargetAccount extends string,
  TProgramAddress extends Address = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
>(
  input: AssertBubblegumTreeConfigAccountInput<TAccountTargetAccount>,
  config?: { programAddress?: TProgramAddress }
): AssertBubblegumTreeConfigAccountInstruction<
  TProgramAddress,
  TAccountTargetAccount
> {
  // Program address.
  const programAddress = config?.programAddress ?? LIGHTHOUSE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    targetAccount: { value: input.targetAccount ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  const instruction = {
    accounts: [getAccountMeta(accounts.targetAccount)],
    programAddress,
    data: getAssertBubblegumTreeConfigAccountInstructionDataEncoder().encode(
      args as AssertBubblegumTreeConfigAccountInstructionDataArgs
    ),
  } as AssertBubblegumTreeConfigAccountInstruction<
    TProgramAddress,
    TAccountTargetAccount
  >;

  return instruction;
}

export type ParsedAssertBubblegumTreeConfigAccountInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly IAccountMeta[] = readonly IAccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Target mpl-bubblegum tree config account to be asserted */
    targetAccount: TAccountMetas[0];
  };
  data: AssertBubblegumTreeConfigAccountInstructionData;
};

export function parseAssertBubblegumTreeConfigAccountInstruction<
  TProgram extends string,
  TAccountMetas extends readonly IAccountMeta[],
>(
  instruction: IInstruction<TProgram> &
    IInstructionWithAccounts<TAccountMetas> &
    IInstructionWithData<Uint8Array>
): ParsedAssertBubblegumTreeConfigAccountInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = instruction.accounts![accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      targetAccount: getNextAccount(),
    },
    data: getAssertBubblegumTreeConfigAccountInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
